// Student agency profile UI: intro video, photo slots, example-photo modal, upload overlay.
import React, { useEffect, useState } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { Link, useNavigate } from "react-router-dom";
import VideoUploadFlowPopup from "commons/popup/VideoUploadFlowPopup.component";
import VideoPlayer from "commons/video/VideoPlayer";

const EXAMPLE_FACE_FRONT = "/imgs/common/portrait/nana-head-front.png";
const EXAMPLE_FACE_LEFT = "/imgs/common/portrait/nana-head-left.png";
const EXAMPLE_FACE_RIGHT = "/imgs/common/portrait/nana-head-right.png";
const EXAMPLE_BODY_FRONT = "/imgs/common/portrait/nana-body-front.png";
const EXAMPLE_BODY_LEFT = "/imgs/common/portrait/nana-body-left.png";
const EXAMPLE_BODY_RIGHT = "/imgs/common/portrait/nana-body-right.png";

type AgencyProfileExamplePhotoModalProps = {
    src: string | null;
    onClose: () => void;
    titleLabel: string;
    imageAlt: string;
    closeLabel: string;
};

function AgencyProfileExamplePhotoModal(props: AgencyProfileExamplePhotoModalProps) {
    const { src, onClose, titleLabel, imageAlt, closeLabel } = props;
    if (!src) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agency-example-photo-modal-title"
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-[1px]"
                onClick={onClose}
                aria-label={closeLabel}
            />
            <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <h2 id="agency-example-photo-modal-title" className="text-base font-semibold text-gray-900">
                        {titleLabel}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        aria-label={closeLabel}
                    >
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="max-h-[80vh] overflow-y-auto bg-gray-50 p-4">
                    <img src={src} alt={imageAlt} className="mx-auto h-auto w-full max-w-full object-contain" decoding="async" />
                </div>
            </div>
        </div>
    );
}

type AgencyProfilePhotoCellProps = {
    imageInfo: any;
    positionI18nSuffix: string;
    photoUrlPath: string;
    profileImageCategoryCode: string;
    profileImageTypeCode: string;
    inputId: string;
    isOnNetworking: boolean;
    uploadImage: (evt: any, category: string, type: string) => void;
    exampleImageSrc: string;
    onOpenExamplePhoto: (src: string) => void;
};

// Face/full-body slot: view badge when imageCount>0; example button opens modal when count is 0.
function AgencyProfilePhotoCell(props: AgencyProfilePhotoCellProps) {
    const {
        imageInfo,
        positionI18nSuffix,
        photoUrlPath,
        profileImageCategoryCode,
        profileImageTypeCode,
        inputId,
        isOnNetworking,
        uploadImage,
        exampleImageSrc,
        onOpenExamplePhoto,
    } = props;
    const imageCount = Number(imageInfo?.imageCount ?? 0);
    const showViewOverlay = imageCount > 0;
    const hasUploadedImage = Boolean(imageInfo?.url);

    return (
        <div className="space-y-4">
            <div
                className={`relative overflow-hidden rounded-xl bg-gray-100 ${
                    hasUploadedImage ? "" : "aspect-square"
                }`}
            >
                <button
                    type="button"
                    onClick={() => onOpenExamplePhoto(exampleImageSrc)}
                    className="absolute left-2 top-2 z-10 rounded-lg bg-white/70 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-black/10 backdrop-blur-[2px] transition-colors hover:bg-white/85"
                >
                    {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.btnViewExamplePhoto") ||
                        "View example photo"}
                </button>
                <img
                    src={imageInfo?.url || "./imgs/dummy/basic-img-3.png"}
                    alt=""
                    className={
                        hasUploadedImage
                            ? "block h-auto w-full object-contain"
                            : "h-full w-full object-cover"
                    }
                />
                {showViewOverlay ? (
                    <div className="absolute right-2 top-2 flex items-center space-x-2 rounded-lg bg-black/50 px-2 py-1">
                        <button
                            type="button"
                            className="flex items-center space-x-1 text-white hover:text-gray-200"
                        >
                            <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span>{imageInfo?.viewCount ?? 0}</span>
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-900">
                    {getLanguageValue(PAGE_TYPE.STUDENT, `agencyProfile.${positionI18nSuffix}`)}
                </p>
                {imageCount > 0 ? (
                    <Link
                        to={`/student/profile/agency/photo/${photoUrlPath}`}
                        className="flex flex-shrink-0 items-center space-x-2 text-gray-600 hover:text-gray-800"
                    >
                        <svg
                            className="h-5 w-5"
                            fill="white"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                        >
                            <rect x="4" y="4" width="12" height="12" strokeWidth={2} />
                            <rect x="8" y="8" width="12" height="12" strokeWidth={2} />
                        </svg>
                        <span className="text-sm">{imageCount}</span>
                    </Link>
                ) : null}
            </div>
            <input
                type="file"
                id={inputId}
                onChange={(evt) => uploadImage(evt, profileImageCategoryCode, profileImageTypeCode)}
                accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
                className="hidden"
            />
            <button
                type="button"
                disabled={isOnNetworking}
                onClick={() => document.getElementById(inputId)?.click()}
                className="block w-full rounded-xl bg-[#CF3688] px-4 py-3 text-center text-sm font-medium !text-white transition-colors hover:bg-[#B82F77] disabled:cursor-not-allowed disabled:opacity-50"
            >
                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.btnUpload")}
            </button>
        </div>
    );
}

const AgencyProfileComponent = (props: any) => {
    const navigate = useNavigate();

    const {
        isOnNetworking,
        profile,
        toggleActiveProfile,
        inputRef,
        videoInfo,
        faceFrontImageInfo,
        faceLeftImageInfo,
        faceRightImageInfo,
        fullBodyFrontImageInfo,
        fullBodyLeftImageInfo,
        fullBodyRightImageInfo,
        uploadVideo,
        videoRef,
        uploadImage,
        openViewPopup,
        handleEnded,
        handleSeeked,
        handlePause,
        handlePlay,

        isProfileActive,
        onChangeIsProfileActive,
        introVideoUploadFlow,
        onCancelIntroVideoUpload,
        onCloseIntroVideoUpload,
        onSaveIntroVideoUpload,
        onSelectIntroVideoUploadThumbnail,
    } = props;

    const [examplePhotoModalSrc, setExamplePhotoModalSrc] = useState<string | null>(null);

    useEffect(() => {
        if (!examplePhotoModalSrc) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setExamplePhotoModalSrc(null);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [examplePhotoModalSrc]);

    useEffect(() => {
        if (!examplePhotoModalSrc) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [examplePhotoModalSrc]);

    const examplePhotoTitle =
        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.btnViewExamplePhoto") || "View example photo";
    const examplePhotoImageAlt =
        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.examplePhotoModalImageAlt") || examplePhotoTitle;
    const examplePhotoCloseLabel =
        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.examplePhotoModalClose") || "Close";

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">{getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtTitle")}</h2>
                <p className="text-gray-600 mb-8">{getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtDesc")}</p>

                <div className="max-w-3xl mx-auto space-y-6">
                    {/* Introduction Video 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtIntroVideoTitle")}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtIntroVideoDesc")}
                            </p>
                            <div
                                className={`w-full overflow-hidden rounded-xl bg-gray-100 ${
                                    videoInfo?.url ? "" : "aspect-video"
                                }`}
                            >
                                {videoInfo?.url ? (
                                    <VideoPlayer
                                        video={videoInfo}
                                        className="block max-h-[70vh] w-full h-auto"
                                        enableVideoLog={false}
                                    />
                                ) : (
                                    <img
                                        src="/imgs/student/v2/empty-agency-intro-video.svg"
                                        alt={
                                            getLanguageValue(
                                                PAGE_TYPE.STUDENT,
                                                "agencyProfile.imgIntroVideoEmptyAlt"
                                            ) ||
                                            "No intro video yet. Upload one below."
                                        }
                                        className="block h-full w-full object-contain object-center"
                                        decoding="async"
                                    />
                                )}
                            </div>
                            <div className="mt-4 flex justify-end items-center">
                                <Link
                                    to="/student/profile/agency/intro-video"
                                    className="flex items-center space-x-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                    <svg className="w-5 h-5" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="4" y="4" width="12" height="12" strokeWidth={2} />
                                        <rect x="8" y="8" width="12" height="12" strokeWidth={2} />
                                    </svg>
                                    <span className="text-sm">{videoInfo?.videoCount || 0}</span>
                                </Link>
                                <button
                                    // onClick={() => openViewPopup(videoInfo?.id, "introVideo")}
                                    className="flex items-center space-x-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    <span className="font-medium">{videoInfo?.viewCount || 0}</span>
                                </button>
                            </div>
                            <div className="mt-4">
                                <input
                                    type="file"
                                    id="input-file-video"
                                    onChange={uploadVideo}
                                    accept="video/*"
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    disabled={isOnNetworking}
                                    onClick={() => document.getElementById("input-file-video").click()}
                                    className="block w-full px-4 py-3 text-sm font-medium text-center !text-white bg-[#CF3688] rounded-xl hover:bg-[#B82F77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.btnUpload")}
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Face Photo 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtFacePhotoTitle")}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtFacePhotoDesc")}
                            </p>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:grid-cols-3">
                                <AgencyProfilePhotoCell
                                    imageInfo={faceFrontImageInfo}
                                    positionI18nSuffix="txtFront"
                                    photoUrlPath="face/front"
                                    profileImageCategoryCode="profileImageCategory:face"
                                    profileImageTypeCode="profileImageType:front"
                                    inputId="input-file-image-face-front"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_FACE_FRONT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                                <AgencyProfilePhotoCell
                                    imageInfo={faceLeftImageInfo}
                                    positionI18nSuffix="txtLeft"
                                    photoUrlPath="face/left"
                                    profileImageCategoryCode="profileImageCategory:face"
                                    profileImageTypeCode="profileImageType:left"
                                    inputId="input-file-image-face-left"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_FACE_LEFT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                                <AgencyProfilePhotoCell
                                    imageInfo={faceRightImageInfo}
                                    positionI18nSuffix="txtRight"
                                    photoUrlPath="face/right"
                                    profileImageCategoryCode="profileImageCategory:face"
                                    profileImageTypeCode="profileImageType:right"
                                    inputId="input-file-image-face-right"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_FACE_RIGHT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Full Body Photo 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtFullBodyPhotoTitle")}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.txtFullBodyPhotoDesc")}
                            </p>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:grid-cols-3">
                                <AgencyProfilePhotoCell
                                    imageInfo={fullBodyFrontImageInfo}
                                    positionI18nSuffix="txtFront"
                                    photoUrlPath="fullBody/front"
                                    profileImageCategoryCode="profileImageCategory:fullBody"
                                    profileImageTypeCode="profileImageType:front"
                                    inputId="input-file-image-body-front"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_BODY_FRONT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                                <AgencyProfilePhotoCell
                                    imageInfo={fullBodyLeftImageInfo}
                                    positionI18nSuffix="txtLeft"
                                    photoUrlPath="fullBody/left"
                                    profileImageCategoryCode="profileImageCategory:fullBody"
                                    profileImageTypeCode="profileImageType:left"
                                    inputId="input-file-image-body-left"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_BODY_LEFT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                                <AgencyProfilePhotoCell
                                    imageInfo={fullBodyRightImageInfo}
                                    positionI18nSuffix="txtRight"
                                    photoUrlPath="fullBody/right"
                                    profileImageCategoryCode="profileImageCategory:fullBody"
                                    profileImageTypeCode="profileImageType:right"
                                    inputId="input-file-image-body-right"
                                    isOnNetworking={isOnNetworking}
                                    uploadImage={uploadImage}
                                    exampleImageSrc={EXAMPLE_BODY_RIGHT}
                                    onOpenExamplePhoto={setExamplePhotoModalSrc}
                                />
                            </div>
                        </div>
                    </section>

                    {/* 기획사용 프로필 활성화 여부 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    {/* <h3 className="text-lg font-semibold text-gray-900">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "profile.txtAgencyProfile")}
                                    </h3> */}
                                    <p className="mt-1 text-sm text-gray-600">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "profile.txtAgencyProfileDesc")}
                                    </p>
                                </div>
                                <div className="relative inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={isProfileActive ?? false}
                                        onChange={(e) => onChangeIsProfileActive(e.target.checked)}
                                        className="sr-only peer"
                                        id="toggle-agency-active"
                                    />
                                    <label
                                        htmlFor="toggle-agency-active"
                                        className="flex items-center w-14 h-7 bg-gray-200 rounded-full cursor-pointer 
                                                             peer-checked:bg-[#CF3688] peer-focus:ring-2 peer-focus:ring-[#CF3688]/20 
                                                             after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                                             after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all
                                                             after:shadow-sm peer-checked:after:translate-x-7"
                                    >
                                        <span className="sr-only">
                                            {getLanguageValue(PAGE_TYPE.STUDENT, "profile.txtAgencyProfileActivate")}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-100">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "profile.txtAgencyProfileExplainTitle")}
                                </h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start text-sm text-gray-600">
                                        <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "profile.txtAgencyProfileExplainDesc1")}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <VideoUploadFlowPopup
                isVisible={Boolean(introVideoUploadFlow?.isVisible)}
                phase={introVideoUploadFlow?.phase || "uploading"}
                progress={introVideoUploadFlow?.progress || 0}
                currentStep={introVideoUploadFlow?.currentStep || ""}
                canCancel={Boolean(introVideoUploadFlow?.canCancel)}
                errorMessage={introVideoUploadFlow?.errorMessage || ""}
                thumbnails={introVideoUploadFlow?.thumbnails || []}
                selectedIndex={introVideoUploadFlow?.selectedIndex || 0}
                isSaving={Boolean(introVideoUploadFlow?.isSaving)}
                labels={{
                    close:
                        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.uploadIntroVideoDismissError") ||
                        "Close",
                    save:
                        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.uploadIntroVideoSave") ||
                        "Save",
                    uploading:
                        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.uploadIntroVideoPleaseWait") ||
                        "Please wait",
                    thumbnailTitle:
                        getLanguageValue(PAGE_TYPE.STUDENT, "agencyProfile.uploadIntroVideoThumbnailTitle") ||
                        "Choose thumbnail",
                }}
                onCancelUpload={onCancelIntroVideoUpload}
                onClose={onCloseIntroVideoUpload}
                onSave={onSaveIntroVideoUpload}
                onSelectThumbnail={onSelectIntroVideoUploadThumbnail}
            />

            <AgencyProfileExamplePhotoModal
                src={examplePhotoModalSrc}
                onClose={() => setExamplePhotoModalSrc(null)}
                titleLabel={examplePhotoTitle}
                imageAlt={examplePhotoImageAlt}
                closeLabel={examplePhotoCloseLabel}
            />
        </main>
    );
};
export default AgencyProfileComponent;
